<template>
  <Head :title="course.name" />
  <div class="md:flex md:flex-row h-full">
    <Drawer v-model:visible="visible" class="lg:hidden border-0">
      <template #container="{ closeCallback }">
        <div class="w-65 sm:w-80 border-r-2 h-full">
          <DrawerToggleButton
            class="-right-2"
            direction="left"
            :on-click="closeCallback"
          />
          <SidebarSection
            :course="course"
            :marked-list="markedList"
            :current-subject-id="subject.id"
          />
        </div>
      </template>
    </Drawer>

    <div class="hidden lg:block relative w-80 h-full">
      <SidebarSection
        :course="course"
        :marked-list="markedList"
        :current-subject-id="subject.id"
      />
    </div>

    <div class="bg-white md:grow h-full relative">
      <DrawerToggleButton
        class="-left-1"
        direction="right"
        :on-click="
          () => {
            visible = true;
          }
        "
      />
      <div class="overflow-y-auto h-full absolute w-full">
        <div
          class="py-4 xl:py-6 2xl:py-12 px-4 md:px-12 flex flex-col items-center justify-center gap-2 lg:gap-4 w-full"
        >
          <div class="px-1 mb-4 flex max-w-3xl justify-start">
            <h1 class="text-lg xl:text-xl font-bold">
              {{ subject.name }}
            </h1>
          </div>
          <template v-if="subject.type === 'VideoSubject'">
            <div class="w-full lg:w-2xl xl:w-3xl 2xl:w-4xl">
              <video-player
                v-if="subject.video"
                :video="subject.video"
                :poster="subject.poster"
                controls-list="nodownload"
                :store-data="true"
                @contextmenu.prevent
              />
            </div>
            <div class="w-full lg:w-2xl xl:w-3xl 2xl:w-4xl">
              <ul class="max-w-3xl">
                <li
                  v-for="key in Object.keys(subject.texts)"
                  :key="key"
                  class="list-disc mt-2 ml-4 sm:ml-6 sm:mr-2"
                >
                  {{ subject.texts[key] }}
                </li>
              </ul>
            </div>
          </template>
          <template v-else-if="subject.type === 'DocumentsSubject'">
            <div class="w-full flex flex-col">
              <a
                v-for="(document, index) in subject.documents"
                :key="index"
                :href="document.url"
                target="_blank"
                class="my-2"
                download="document.filename"
              >
                •<span class="pl-1 underline">{{ document.filename }}</span
                ><i class="pi pi-download ml-2"></i>
              </a>
            </div>
          </template>
          <template v-else-if="subject.type === 'FeedbackSubject'">
            <!-- Files Section -->
            <div class="w-full lg:w-2xl xl:w-3xl 2xl:w-4xl">
              <!-- Sorted and grouped files -->
              <div
                v-if="
                  subject.feedback.file_groups &&
                  subject.feedback.file_groups.length > 0
                "
                class="mb-8"
              >
                <h2 class="text-lg font-semibold mb-4 text-slate-800">
                  Fisiere
                </h2>
                <template
                  v-for="(group, idx) in subject.feedback.file_groups"
                  :key="idx"
                >
                  <div class="mb-6">
                    <div
                      class="mb-2 px-3 py-1 bg-slate-200 rounded text-sm font-medium text-slate-700"
                    >
                      {{
                        group.type === 'uploader'
                          ? 'Fisiere incarcate de mine'
                          : 'Feedback'
                      }}
                    </div>
                    <div class="space-y-2">
                      <template v-for="file in group.files" :key="file.id">
                        <FileItem
                          :file="file"
                          variant="list"
                          class="flex items-center"
                        />
                      </template>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Empty state -->
              <div v-else class="mb-8 text-center text-slate-600">
                <p>Nu sunt fisiere incarcate inca.</p>
              </div>

              <!-- Completion Notice -->
              <div v-if="subject.feedback.completed" class="mb-8">
                <div
                  class="border border-secondary-color rounded-lg p-4 text-center"
                >
                  <i
                    class="pi pi-check-circle text-secondary-color text-2xl mb-2"
                  ></i>
                  <p class="text-secondary-color font-semibold">
                    Feedback finalizat
                  </p>
                </div>
              </div>

              <!-- File Uploader -->
              <div v-else class="mb-8">
                <h2 class="text-lg font-semibold mb-4 text-slate-800">
                  Incarca fisiere
                </h2>
                <FileUploader
                  ref="fileUploader"
                  v-model:files="uploadedFiles"
                  :user-side="true"
                  :max-file-size="10485760"
                />
                <div
                  v-if="uploadError"
                  class="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"
                >
                  {{ uploadError }}
                </div>
                <Button
                  v-if="uploadedFiles.length > 0"
                  class="mt-4 bg-secondary-color hover:bg-blue-950"
                  :loading="uploading"
                  icon="pi pi-upload"
                  :label="`Salveaza ${uploadedFiles.length} fisier${
                    uploadedFiles.length > 1 ? 'e' : ''
                  }`"
                  @click="submitFiles"
                />
              </div>
            </div>
          </template>
          <template v-else-if="subject.type === 'QuizSubject'">
            <div class="w-full lg:w-2xl xl:w-3xl 2xl:w-4xl">
              <!-- EXAM TYPE -->
              <template v-if="subject.quiz_type === 'exam'">
                <exam-section
                  :subject="subject"
                  :pass-icon="passIcon"
                  :fail-icon="failIcon"
                  :quiz-icon="quizIcon"
                  @update:is-in-progress="isExamInProgress = $event"
                />
              </template>

              <!-- REGULAR QUIZ (practice/course) -->
              <template v-else>
                <!-- Quiz not started -->
                <div v-if="!quizStarted" class="text-center pb-8">
                  <img :src="quizIcon" class="w-16 h-16 mx-auto mb-4" />
                  <p class="text-gray-600 mb-6">
                    Numar total intrebari:
                    {{ subject.quiz_questions?.length || 0 }}
                  </p>
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    v-if="subject.description"
                    class="prose max-w-none mb-4 text-left"
                    v-html="sanitizedDescription"
                  ></div>
                  <!-- eslint-enable vue/no-v-html -->
                  <button
                    class="bg-secondary-color hover:bg-blue-950 py-3 px-8 rounded-lg text-white font-semibold"
                    @click="startQuiz"
                  >
                    Incepe testarea
                  </button>
                </div>

                <!-- Quiz in progress -->
                <div
                  v-else-if="currentQuestionIndex < quizQuestions.length"
                  class="py-8"
                >
                  <div class="mb-4">
                    <span class="text-sm text-gray-600">
                      Intrebarea {{ currentQuestionIndex + 1 }} din
                      {{ quizQuestions.length }}
                    </span>
                    <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        class="bg-secondary-color h-2 rounded-full transition-all"
                        :style="{
                          width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%`,
                        }"
                      ></div>
                    </div>
                  </div>

                  <h3 class="text-xl font-semibold mb-6">
                    {{ currentQuestion.text }}
                  </h3>

                  <div class="space-y-3">
                    <button
                      v-for="(answer, idx) in currentQuestion.answers"
                      :key="idx"
                      class="w-full text-left p-4 rounded-lg border-2 transition-all hover:border-secondary-color hover:bg-blue-50 flex items-center gap-3"
                      :class="{
                        'border-secondary-color bg-blue-50':
                          selectedAnswer === answer,
                        'border-gray-300': selectedAnswer !== answer,
                      }"
                      @click="selectAnswer(answer)"
                    >
                      <div
                        class="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                        :class="{
                          'border-secondary-color bg-secondary-color':
                            selectedAnswer === answer,
                          'border-gray-400': selectedAnswer !== answer,
                        }"
                      >
                        <i
                          v-if="selectedAnswer === answer"
                          class="pi pi-check text-white text-xs"
                        ></i>
                      </div>
                      <span>{{ answer }}</span>
                    </button>
                  </div>

                  <div class="mt-6 flex justify-end">
                    <button
                      :disabled="!selectedAnswer"
                      class="bg-secondary-color hover:bg-blue-950 py-3 px-8 rounded-lg text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      @click="nextQuestion"
                    >
                      {{
                        currentQuestionIndex === quizQuestions.length - 1
                          ? 'Finalizeaza'
                          : 'Urmatoarea intrebare'
                      }}
                    </button>
                  </div>
                </div>

                <!-- Checking results (loading) -->
                <div v-else-if="checkingResults" class="text-center py-8">
                  <div class="mb-6 flex justify-center">
                    <i
                      class="pi pi-spin pi-spinner text-secondary-color text-6xl"
                    ></i>
                  </div>
                  <h2 class="text-2xl font-bold mb-4 text-secondary-color">
                    Procesam rezultatele
                  </h2>
                  <p class="text-gray-600">Te rugam sa astepti...</p>
                </div>

                <!-- Quiz completed -->
                <div v-else class="text-center py-8">
                  <div class="mb-6 flex justify-center">
                    <img
                      :src="quizPassed ? passIcon : failIcon"
                      :alt="quizPassed ? 'Pass' : 'Fail'"
                      class="w-32 h-32 object-contain"
                    />
                  </div>
                  <div
                    class="text-4xl font-bold mb-2"
                    :style="{ color: quizPassed ? '#92D050' : '#D1805D' }"
                  >
                    {{ score }} / {{ totalQuestions }} ({{
                      parseFloat(scorePercentage).toFixed(1)
                    }}%)
                  </div>
                  <h2 class="text-2xl font-bold mb-4">
                    {{ quizCompletionTitle }}
                  </h2>
                  <p class="text-gray-600 mb-6">
                    {{ quizCompletionMessage }}
                  </p>
                  <button
                    v-if="!quizPassed"
                    class="bg-secondary-color hover:bg-blue-950 py-3 px-8 rounded-lg text-white font-semibold"
                    @click="restartQuiz"
                  >
                    Reincepe testarea
                  </button>
                </div>
              </template>
            </div>
          </template>
          <div
            v-if="
              !isExamInProgress &&
              (!quizStarted ||
                (currentQuestionIndex >= quizQuestions.length &&
                  !checkingResults))
            "
            class="flex flex-col gap-2 sm:gap-0 sm:flex-row sm:justify-between w-full lg:w-2xl xl:w-3xl mt-5"
          >
            <Link
              :href="
                subject.previous_id ? $routes.subject(subject.previous_id) : ''
              "
              class="rounded-3xl field-primary-colors py-3 pr-4 pl-3 text-md border text-white flex items-center justify-center"
              :class="subject.previous_id ? '' : 'invisible'"
              @click="
                handleLinkClick($event, $routes.subject(subject.previous_id))
              "
            >
              <i class="pi pi-chevron-left mr-1" />Subiectul anterior
            </Link>
            <button
              v-if="subject.type === 'VideoSubject'"
              class="bg-secondary-color hover:bg-blue-950 py-3 px-2 md:px-3 rounded-3xl cursor-pointer text-white flex items-center justify-center"
              type="button"
              @click="
                markedList.includes(subject.id)
                  ? markUnfinished()
                  : markFinished()
              "
            >
              {{
                markedList.includes(subject.id)
                  ? 'Marcheaza nefinalizat'
                  : 'Marcheaza finalizat'
              }}<i class="pi pi-check ml-1" />
            </button>
            <Link
              :href="subject.next_id ? $routes.subject(subject.next_id) : ''"
              class="rounded-3xl field-primary-colors py-3 pl-4 pr-3 text-md border text-white flex items-center justify-center"
              :class="subject.next_id ? '' : 'invisible'"
              @click="handleLinkClick($event, $routes.subject(subject.next_id))"
            >
              Subiectul următor<i class="pi pi-chevron-right ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Custom Confirmation Dialog -->
  <div
    v-if="showConfirmDialog"
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-300/75"
    @click.self="cancelNavigation"
  >
    <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
      <h3 class="text-lg font-semibold mb-4 text-gray-900">
        Confirmă navigarea
      </h3>
      <p class="text-gray-600 mb-6">
        Progresul evaluării va fi pierdut. Sigur doriți să părăsiți această
        pagină?
      </p>
      <div class="flex justify-end gap-3">
        <button
          class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
          @click="cancelNavigation"
        >
          Anulează
        </button>
        <button
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
          @click="confirmNavigation"
        >
          Confirmă
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import VideoPlayer from '@/Shared/VideoPlayer.vue';
import FileUploader from '@/Shared/FileUploader.vue';
import FileItem from '@/Shared/FileItem.vue';
import Button from 'primevue/button';
import axios from 'axios';
import { quizResultPercentage, quizResultPassed } from '@/utils/formatters';
import Drawer from 'primevue/drawer';

import DrawerToggleButton from './DrawerToggleButton.vue';
import SidebarSection from './SidebarSection.vue';
import ExamSection from './ExamSection.vue';

// Quiz result icons
import passIcon from '@/images/Test_Pass.png';
import failIcon from '@/images/Test_Fail.png';
import quizIcon from '@/images/Test_intro.png';
import sanitizeHtml from 'sanitize-html';

export default {
  components: {
    Head,
    Link,
    VideoPlayer,
    FileUploader,
    FileItem,
    Button,
    Drawer,
    DrawerToggleButton,
    SidebarSection,
    ExamSection,
  },

  beforeRouteLeave(to, from, next) {
    // Only prevent if quiz (not exam) is in progress - exam answers are always saved
    if (
      this.quizStarted &&
      this.currentQuestionIndex < this.quizQuestions.length
    ) {
      this.pendingNavigation = { url: to.href, next: next };
      this.showConfirmDialog = true;
      return false;
    }
    next();
  },
  layout: Layout,
  props: {
    course: {
      type: Object,
      required: true,
    },
    subject: {
      type: Object,
      required: true,
    },
    marked: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      videoOptions: {
        sources: [
          {
            src: this.subject.video,
            type: 'video/mp4',
          },
        ],
      },
      visible: false,
      markedList: [...this.marked],
      uploadedFiles: [],
      uploading: false,
      pollInterval: null,
      uploadError: null,
      // Quiz state
      quizStarted: false,
      currentQuestionIndex: 0,
      quizQuestions: [],
      userAnswers: {}, // Store user's answers { questionId: selectedAnswer }
      score: 0,
      totalQuestions: 0,
      resultPercentage: 0,
      selectedAnswer: null,
      checkingResults: false,
      handleBeforeUnload: null,
      showConfirmDialog: false,
      pendingNavigation: null,
      removeInertiaListener: null,
      // Exam state (managed by ExamSection component)
      isExamInProgress: false,
      passIcon,
      failIcon,
      quizIcon,
    };
  },

  computed: {
    sanitizedDescription() {
      return sanitizeHtml(this.subject.description || '', {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
        allowedAttributes: {
          ...sanitizeHtml.defaults.allowedAttributes,
          '*': ['class', 'style'],
        },
      });
    },
    currentQuestion() {
      return this.quizQuestions[this.currentQuestionIndex];
    },
    scorePercentage() {
      return this.resultPercentage;
    },
    quizPassed() {
      return quizResultPassed(this.score, this.totalQuestions);
    },
    quizCompletionTitle() {
      if (this.subject.quiz_type === 'course') {
        return this.quizPassed ? 'Finalizat cu Succes!' : 'Nepromovat!';
      } else {
        return this.quizPassed
          ? 'Punctaj min. indeplinit!'
          : 'Punctaj min. neindeplinit!';
      }
    },
    quizCompletionMessage() {
      if (this.subject.quiz_type === 'course') {
        return this.quizPassed ? 'Felicitari!' : 'Va rugam sa reluati testul!';
      } else {
        return this.quizPassed
          ? 'Felicitari!'
          : 'Va sugeram sa reluati testul!';
      }
    },
  },

  watch: {
    'subject.feedback.file_groups': {
      deep: true,
      handler() {
        if (this.hasPendingFiles()) {
          if (!this.pollInterval) {
            this.startPolling();
          }
        } else {
          this.stopPolling();
        }
      },
    },
    quizStarted(newVal) {
      if (newVal && this.currentQuestionIndex < this.quizQuestions.length) {
        this.addNavigationWarning();
      } else {
        this.removeNavigationWarning();
      }
    },
    currentQuestionIndex(newVal) {
      if (this.quizStarted && newVal >= this.quizQuestions.length) {
        this.removeNavigationWarning();
      }
    },
  },

  mounted() {
    this.startPolling();
    // Set up Inertia navigation listener
    this.removeInertiaListener = this.$inertia.on('before', (event) => {
      if (
        this.quizStarted &&
        this.currentQuestionIndex < this.quizQuestions.length
      ) {
        event.preventDefault();
        this.pendingNavigation = { url: event.detail.visit.url };
        this.showConfirmDialog = true;
      }
    });
    // Resume exam if in progress from server - handled by ExamSection
  },

  beforeUnmount() {
    this.stopPolling();
    this.removeNavigationWarning();
    // Remove Inertia listener
    if (this.removeInertiaListener) {
      this.removeInertiaListener();
    }
  },

  methods: {
    isUrl(...urls) {
      let currentUrl = this.$page.url.substr(1);

      if (urls[0] === '') {
        return currentUrl === '';
      }

      return urls.filter((url) => currentUrl.startsWith(url)).length;
    },
    markFinished() {
      let self = this;
      axios
        .post(this.$routes.user_subjects(), {
          subject_id: this.subject.id,
        })
        .then(function () {
          self.markedList.push(self.subject.id);
        })
        // TODO: handle the error
        .catch(function (error) {
          console.log(error);
        });
      return null;
    },
    markUnfinished() {
      let self = this;
      axios
        .delete(this.$routes.user_subject(self.subject.id))
        .then(function () {
          self.markedList = self.markedList.filter(
            (value) => value !== self.subject.id,
          );
        })
        // TODO: handle the error
        .catch(function (error) {
          console.log(error);
        });
      return null;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    submitFiles() {
      if (!this.subject.feedback || this.uploadedFiles.length === 0) return;

      this.uploading = true;
      this.uploadError = null;
      const formData = new FormData();
      this.uploadedFiles.forEach((file) => {
        formData.append('uploader_files[]', file);
      });
      this.$inertia.patch(
        this.$routes.feedback(this.subject.feedback.id),
        formData,
        {
          onSuccess: () => {
            if (this.$page.props.errors?.base) {
              this.uploadError = 'Eroare la incarcarea fisierelor';
            } else {
              this.uploadedFiles = [];
              this.$refs.fileUploader.$refs.fileUpload.clear();
            }
          },
          onError: (errors) => {
            if (errors?.base == 'Upload limit exeeded') {
              this.uploadError =
                'Poti incarca maximum 15 fisiere in 24 de ore.';
            } else {
              this.uploadError =
                errors?.base ||
                this.$page.props.errors?.base ||
                'Eroare la incarcarea fisierelor';
            }
          },
          onFinish: () => {
            this.uploading = false;
          },
        },
      );
    },
    hasPendingFiles() {
      if (!this.subject.feedback || !this.subject.feedback.file_groups) {
        return false;
      }
      return this.subject.feedback.file_groups.some((group) =>
        group.files.some((file) => !file.scanned),
      );
    },
    startPolling() {
      if (this.hasPendingFiles()) {
        this.pollInterval = setInterval(() => {
          this.$inertia.reload();
        }, 3000);
      }
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    // Quiz methods
    startQuiz() {
      this.quizStarted = true;
      this.currentQuestionIndex = 0;
      this.userAnswers = {};
      this.score = 0;
      this.totalQuestions = 0;
      this.resultPercentage = 0;
      this.selectedAnswer = null;
      this.checkingResults = false;
      // Shuffle questions - answers are already shuffled from backend
      const shuffledQuestions = this.shuffleArray(
        this.subject.quiz_questions || [],
      );
      this.quizQuestions = shuffledQuestions;
      this.addNavigationWarning();
    },
    selectAnswer(answer) {
      this.selectedAnswer = answer;
    },
    nextQuestion() {
      if (!this.selectedAnswer) return;

      // Store user's answer
      this.userAnswers[this.currentQuestion.id] = this.selectedAnswer;

      this.selectedAnswer = null;
      this.currentQuestionIndex++;

      // If quiz is complete, check answers
      if (this.currentQuestionIndex >= this.quizQuestions.length) {
        this.checkQuizAnswers();
      }
    },
    async checkQuizAnswers() {
      this.checkingResults = true;
      try {
        const response = await axios.post(
          this.$routes.check_answers_subject(this.subject.id),
          {
            answers: this.userAnswers,
          },
        );

        this.score = response.data.correct_count;
        this.totalQuestions = response.data.total_count;
        this.resultPercentage = quizResultPercentage(
          this.score,
          this.totalQuestions,
        );
      } catch (error) {
        console.error('Error checking quiz answers:', error);
        // Fallback: show completion anyway
        this.score = 0;
        this.totalQuestions = this.quizQuestions.length;
        this.resultPercentage = 0;
      } finally {
        this.checkingResults = false;
      }
    },
    restartQuiz() {
      this.startQuiz();
    },
    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    addNavigationWarning() {
      this.handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = '';
      };
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
    removeNavigationWarning() {
      if (this.handleBeforeUnload) {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this.handleBeforeUnload = null;
      }
    },
    handleLinkClick(event, url) {
      // Check if quiz or exam is in progress
      if (
        (this.quizStarted &&
          this.currentQuestionIndex < this.quizQuestions.length) ||
        this.isExamInProgress
      ) {
        event.preventDefault();
        this.pendingNavigation = { url: url };
        this.showConfirmDialog = true;
      }
    },
    confirmNavigation() {
      // User confirmed - proceed with navigation
      this.showConfirmDialog = false;

      // Remove listeners before navigating
      this.removeNavigationWarning();
      if (this.removeInertiaListener) {
        this.removeInertiaListener();
      }

      if (this.pendingNavigation && this.pendingNavigation.url) {
        // Navigate to the intended URL
        this.$inertia.visit(this.pendingNavigation.url);
        this.pendingNavigation = null;
      }
    },
    cancelNavigation() {
      // User cancelled - stay on page
      this.showConfirmDialog = false;
      this.pendingNavigation = null;
    },
  },
};
</script>
